import { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { PAYMENT_NEW, PAYMENT_SUCCESS } from '../../constants/payment'
import { useAppParamsContext } from '../../context/AppParamsContext'
import { useAppUserContext } from '../../context/AppUserContext'
import appUserService from '../../services/appUserService'
import useExternalPaymentRedirect from './useExternalPaymentRedirect'
import { parseFromLocalStorage } from '../../helper/localStorage'
import { buildReferralPayload } from '../../helper/referralParams'
import { usePartnerStrategy } from './partner'

const PARTNER_TYPE = {
  DEFAULT: 'DEFAULT',
  VIETTEL_MONEY: 'VIETTEL_MONEY'
}

const ERROR_CREATE_PAYMENT = {
  UNCONFIRMED_BOOKING_EXISTED: 'Phương tiện của quý khách đã đăng ký thông báo phạt nguội. Vui lòng kiểm tra lại.',
  MAX_LIMIT_SCHEDULE_BY_USER: 'Bạn đã đạt giới hạn số đơn hàng cho phép. Vui lòng liên hệ hỗ trợ để được giải quyết.',
  ORDER_NOT_FOUND: 'Đơn hàng không tồn tại.',
  USER_NOT_FOUND: 'Người dùng không tồn tại.',
  CREATE_PAYMENT_FAILED: 'Tạo đơn hàng thanh toán không thành công.',
  NO_LINK_ACCOUNT_WITH_VTM: 'Chưa liên kết tài khoản Viettel Money.',
  UNAVAILABLE_BALANCE: 'Số dư không khả dụng.',
  PAYMENT_FAILED: 'Thanh toán thất bại.'
}

export function getTrafficFinePaymentPartnerType() {
  if (parseFromLocalStorage(process.env.REACT_APP_MINIAPP_VIETTEL_MONEY)) {
    return PARTNER_TYPE.VIETTEL_MONEY
  }

  return PARTNER_TYPE.DEFAULT
}

export function shouldHandleTrafficFinePaymentAtVehicleStep() {
  const partnerType = getTrafficFinePaymentPartnerType()

  if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
    return true
  }

  return false
}

export const convertListLicensePlates = (vehicles = []) =>
  vehicles.map((vehicle) => (typeof vehicle === 'string' ? vehicle : vehicle?.plate)).filter(Boolean)

export const convertListProductIds = (services = []) =>
  services.map((service) => (Number(service?.productSize) > 0 ? { productId: service?.productId, quantity: 1 } : service?.productId)).filter(Boolean)

function getPaymentMethodData(paymentMethod, paymentMethodId, paymentMethodType) {
  if (paymentMethod && typeof paymentMethod === 'object') {
    return {
      paymentMethodId: paymentMethod?.paymentMethodId,
      paymentMethodType: paymentMethod?.paymentMethodType
    }
  }

  return {
    paymentMethodId: paymentMethodId ?? paymentMethod,
    paymentMethodType
  }
}

function getErrorMessage(errorCode, defaultContent) {
  return {
    title: 'Thanh toán thất bại',
    content: ERROR_CREATE_PAYMENT?.[errorCode] || defaultContent
  }
}

function getReferralRuntimeValue(key, contextValue) {
  const searchValue = new URLSearchParams(window.location.search).get(key)
  const sessionValue = sessionStorage.getItem(key)
  const localValue = localStorage.getItem(key)

  return contextValue || searchValue || sessionValue || localValue
}

export default function useTrafficFinePayment({
  orderDetail = null,
  vehicles = [],
  services = [],
  email,
  paymentMethod = null,
  paymentMethodId,
  paymentMethodType,
  successPath,
  onPaymentSuccess,
  onPaymentPending,
  referUserIdFromInput,
  referUserCodeFromInput
} = {}) {
  const history = useHistory()
  const { hidePaymentMethod, referStationId, referUserId, referStationCode, referUserCode } = useAppParamsContext()
  const { userData } = useAppUserContext()
  const partnerStrategy = usePartnerStrategy()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [paymentDetail, setPaymentDetail] = useState(null)
  const [isOpenQrCodePayment, setIsOpenQrCodePayment] = useState(false)
  // Order id da tao thanh cong, expose ra de call site theo doi trang thai khi QR dang hien.
  const [pendingOrderId, setPendingOrderId] = useState(null)
  const redirectToExternalPayment = useExternalPaymentRedirect(setIsSubmitting)

  const paymentMethodData = useMemo(
    () => getPaymentMethodData(paymentMethod, paymentMethodId, paymentMethodType),
    [paymentMethod, paymentMethodId, paymentMethodType]
  )

  const phoneNumber = useMemo(() => {
    return userData?.phoneNumberWithUsernamePrefix || orderDetail?.phoneNumber || ''
  }, [orderDetail?.phoneNumber, userData?.phoneNumberWithUsernamePrefix])

  const handlePayment = useCallback(async () => {
    try {
      if (isSubmitting) {
        return
      }

      // 1. Nếu đã có đơn thì dùng lại orderId cũ, chưa có thì tạo đơn mới.
      let customerScheduleId = orderDetail?.orderId
      
      if (!customerScheduleId) {
        if (!paymentMethodData?.paymentMethodId && !paymentMethodData?.paymentMethodType) {
          setErrorMessage({
            title: 'Thanh toán thất bại',
            content: 'Vui lòng chọn phương thức thanh toán.'
          })
          return
        }

        const runtimeReferStationId = getReferralRuntimeValue('referStationId', referStationId)
        
        // Priority: UI Input > Context/URL/Storage
        const finalReferUserId = referUserIdFromInput !== undefined ? referUserIdFromInput : getReferralRuntimeValue('referUserId', referUserId)
        const finalReferUserCode = referUserCodeFromInput !== undefined ? referUserCodeFromInput : getReferralRuntimeValue('referUserCode', referUserCode)

        const runtimeReferStationCode = getReferralRuntimeValue('referStationCode', referStationCode)
        const payloadCreateOrder = {
          email,
          licensePlatesList: convertListLicensePlates(vehicles),
          productIds: convertListProductIds(services),
          // Ghi chú: Nếu có sdkToken thì bỏ qua phoneNumber/username.
          ...(userData?.sdkToken ? {} : { phoneNumber }),
          requestId: `REQ-${Date.now()}`,
          allowRenew: partnerStrategy.getAllowRenew(),
          vehicleType: vehicles?.[0]?.vehicleType,
          ...partnerStrategy.getCreateOrderPaymentMethodData(paymentMethodData),
          ...buildReferralPayload({
            referStationId: runtimeReferStationId,
            referUserId: finalReferUserId,
            referStationCode: runtimeReferStationCode,
            referUserCode: finalReferUserCode
          })
        }
        setIsSubmitting(true)
        const orderResult = await appUserService.createOrderAutoNoty(payloadCreateOrder)
        setIsSubmitting(false)

        if (orderResult?.statusCode !== 200 || !orderResult?.data) {
          const nextErrorMessage = getErrorMessage(orderResult?.error, 'Có lỗi xảy ra khi tạo đơn thanh toán. Vui lòng thử lại.')
          setErrorMessage(nextErrorMessage)
          return
        }

        customerScheduleId = orderResult?.data?.orderId
      }

      // 2. Delegate pre-payment check to strategy
      const canProceed = await partnerStrategy.beforePayment({
        orderId: customerScheduleId,
        phoneNumber,
        setIsSubmitting,
        setErrorMessage,
        redirectToExternalPayment
      })
      
      if (!canProceed) {
        return
      }

      // 3. Tạo thanh toán cho đơn vừa có.
      const { returnPath, cancelPath } = partnerStrategy.getRedirectUrls(customerScheduleId, phoneNumber)

      const payloadCreatePayment = {
        customerScheduleId,
        ...partnerStrategy.getCreatePaymentMethodData(paymentMethodData, orderDetail),
        paymentMethodOptions: {
          returnUrl: window.location.origin + returnPath,
          cancelUrl: window.location.origin + cancelPath
        }
      }

      setIsSubmitting(true)
      const paymentResponse = await appUserService.createPayment(payloadCreatePayment)

      if (paymentResponse?.statusCode !== 200 || !paymentResponse?.data) {
        setIsSubmitting(false)
        const nextErrorMessage = getErrorMessage(paymentResponse?.error, 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.')
        setErrorMessage(nextErrorMessage)
        return
      }

      setPaymentDetail(paymentResponse.data)

      // 4. Delegate post-payment handling to strategy
      const postPaymentResult = partnerStrategy.handlePostPaymentResponse({
        paymentResponse,
        setIsSubmitting,
        setErrorMessage,
        redirectToExternalPayment
      })

      if (postPaymentResult?.handled) {
        return
      }

      setIsSubmitting(false)
      if (paymentResponse?.data?.paymentStatus === PAYMENT_SUCCESS) {
        if (typeof onPaymentSuccess === 'function') {
          onPaymentSuccess({
            customerScheduleId,
            paymentDetail: paymentResponse.data
          })
        } else if (successPath) {
          history.push(successPath.replace(':id', customerScheduleId))
        }

        return
      }

      if (paymentResponse?.data?.paymentStatus === PAYMENT_NEW) {
        setPendingOrderId(customerScheduleId)
        setIsOpenQrCodePayment(true)

        if (typeof onPaymentPending === 'function') {
          onPaymentPending({
            customerScheduleId,
            paymentDetail: paymentResponse.data
          })
        }

        return
      }

      const nextErrorMessage = {
        title: 'Thanh toán thất bại',
        content: 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.'
      }
      setErrorMessage(nextErrorMessage)
      return
    } catch (error) {
      setIsSubmitting(false)
      setErrorMessage({
        title: 'Thanh toán thất bại',
        content: 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.'
      })
    }
  }, [
    email,
    history,
    isSubmitting,
    onPaymentPending,
    onPaymentSuccess,
    orderDetail,
    partnerStrategy,
    paymentMethodData,
    phoneNumber,
    redirectToExternalPayment,
    referStationId,
    referUserId,
    referStationCode,
    referUserCode,
    referUserIdFromInput,
    referUserCodeFromInput,
    services,
    successPath,
    vehicles
  ])

  const disabledPayBtn = useMemo(() => {
    // Luôn yêu cầu phải có phương thức thanh toán, trừ khi đã có orderDetail?.orderId (đã tạo đơn)
    const hasPaymentMethod = !!(paymentMethodData?.paymentMethodId || paymentMethodData?.paymentMethodType)
    const missingPaymentMethod = !orderDetail?.orderId && !hasPaymentMethod

    return isSubmitting || vehicles.length === 0 || missingPaymentMethod
  }, [isSubmitting, orderDetail?.orderId, paymentMethodData, vehicles.length])

  return {
    isSubmitting,
    errorMessage,
    setErrorMessage,
    paymentDetail,
    isOpenQrCodePayment,
    setIsOpenQrCodePayment,
    pendingOrderId,
    disabledPayBtn,
    handlePayment
  }
}
